import { UserAuthForm } from "@/pages/authentication/components/auth-form.tsx";
import { BookOpen, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function AuthenticationPage() {
    return (
        <div className="container relative h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0 bg-background overflow-hidden">
            {/* Mobile/Tablet Background */}
            <div className="absolute inset-0 bg-zinc-900 lg:hidden" />

            {/* Left Side - Brand & Aesthetic Area */}
            <div className="relative hidden h-full flex-col bg-zinc-950 p-10 text-white lg:flex dark:border-r border-zinc-800">
                {/* Aurora Background Effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5 }}
                        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-zinc-950 to-zinc-950"
                    />
                    <motion.div
                        animate={{
                            scale: [1, 1.25, 1],
                            x: [0, 50, -20, 0],
                            y: [0, -30, 20, 0]
                        }}
                        transition={{
                            duration: 12,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{ willChange: "transform" }}
                        className="absolute -top-[10%] -right-[10%] w-[800px] h-[800px] rounded-full bg-indigo-600/20 blur-[60px] opacity-40 transform-gpu"
                    />
                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            x: [0, -40, 20, 0],
                            y: [0, 40, -20, 0]
                        }}
                        transition={{
                            duration: 15,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 2
                        }}
                        style={{ willChange: "transform" }}
                        className="absolute top-[30%] -left-[20%] w-[600px] h-[600px] rounded-full bg-violet-600/20 blur-[60px] opacity-40 transform-gpu"
                    />
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            x: [0, 30, -30, 0],
                            y: [0, 30, -20, 0]
                        }}
                        transition={{
                            duration: 18,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 4
                        }}
                        style={{ willChange: "transform" }}
                        className="absolute bottom-[10%] right-[20%] w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-[60px] opacity-30 transform-gpu"
                    />
                </div>

                {/* Brand Logo */}
                <div className="relative z-20 flex items-center text-lg font-medium tracking-tight">
                    <div className="p-2 bg-white/10 backdrop-blur-md rounded-lg mr-2 border border-white/10">
                        <BookOpen className="h-5 w-5" />
                    </div>
                    <span>Plot Studio</span>
                </div>

                {/* Main Content Area */}
                <div className="relative z-20 mt-auto mb-auto max-w-lg">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <h1 className="text-5xl font-serif font-medium tracking-tight leading-[1.1] mb-6 text-balance">
                            Craft your masterpieces with unexpected clarify.
                        </h1>
                        <p className="text-lg text-zinc-400 font-light leading-relaxed">
                            A modern workspace for authors who want to plot, write, and organize without the clutter.
                        </p>
                    </motion.div>
                </div>

                {/* Footer Quote */}
                <div className="relative z-20 mt-auto">
                    <motion.blockquote
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 1 }}
                        className="space-y-2"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-[1px] w-8 bg-zinc-700" />
                            <Sparkles className="h-4 w-4 text-zinc-500" />
                            <div className="h-[1px] w-8 bg-zinc-700" />
                        </div>
                        <p className="text-lg font-serif italic text-zinc-300">
                            &ldquo;I don't plot my books rigidly, follow a preconceived structure. A novel, for me, is a journey of discovery.&rdquo;
                        </p>
                        <footer className="text-sm text-zinc-500 font-medium tracking-wide first-letter:uppercase">— John Fowles</footer>
                    </motion.blockquote>
                </div>
            </div>

            {/* Right Side - Auth Form */}
            <div className="lg:p-8 relative h-full flex items-center justify-center">
                {/* Decorative background for mobile */}
                <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 lg:hidden">
                    <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" style={{ clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)' }}></div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px] p-8 lg:p-0 bg-zinc-900/50 lg:bg-transparent backdrop-blur-xl lg:backdrop-blur-none rounded-2xl lg:rounded-none border border-white/10 lg:border-none shadow-2xl lg:shadow-none"
                >
                    <div className="flex flex-col space-y-2 text-center">
                        <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                            <BookOpen className="h-6 w-6 text-primary" />
                        </div>
                        <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
                        <p className="text-sm text-muted-foreground">
                            Enter your details to access your workspace
                        </p>
                    </div>

                    <UserAuthForm />

                    <p className="px-8 text-center text-xs text-muted-foreground">
                        By clicking continue, you agree to our{" "}
                        <a href="https://plotstudio.com/terms-of-service.html" className="underline underline-offset-4 hover:text-primary">
                            Terms of Service
                        </a>
                        {" "}and{" "}
                        <a href="https://plotstudio.com/privacy-policy.html" className="underline underline-offset-4 hover:text-primary">
                            Privacy Policy
                        </a>
                        .
                    </p>
                </motion.div>
            </div>
        </div>
    )
}
